import { useCallback, useEffect, useState } from 'react';

import type { ErrorWithMessage } from '../../../types/ErrorWithMessage';
import { ensureErrorWithMessage } from '../../../utils/ensureErrorWithMessage';
import request from '../../../utils/request';

type Status = 'Initial' | 'Loading' | 'Failed' | 'Success' | 'Waiting';

export const paymentServiceSuppliers = {
  OEB: 'DanskeBank',
  SHB: 'Handelsbanken',
  ICA: 'Ica',
  LFB: 'Lansforsakringar',
  NB: 'Nordea',
  SBAB: 'SBAB',
  SEB: 'SEB',
  SKB: 'Skandia',
  SYD: 'Sparbanken Syd',
  FSPA: 'Swedbank',
  FSPASB: 'Swedbank Sparbankerna',
} as const;

export type Bank = keyof typeof paymentServiceSuppliers;

interface AutogiroResponseState {
  status: Exclude<Status, 'Initial'>;
  accountNumbers?: {
    Name: string;
    Number: string; // TODO number?
    AutogiroCompatible: string;
  }[];
  error?: ErrorWithMessage;
  qr?: string;
  autoStartToken?: string;
  autoStartTokenIsRequired?: boolean;
  id?: string;
}

const pollingInterval = 1000;
const initialState = {
  status: 'Initial',
} as const;

const shouldPoll = (status?: Status) =>
  status != null && !['Success', 'Failed'].includes(status);

const useGetBankAccounts = ({ orderId }: { orderId: string }) => {
  const [collectId, setCollectId] = useState<string | null>(null);
  const [state, setState] = useState<
    | {
        status: 'Initial';
      }
    | { status: 'Loading' }
    | AutogiroResponseState
  >(initialState);

  // Reset state callback
  const reset = useCallback(() => {
    setCollectId(null);
    setState(initialState);
  }, []);

  // Start by calling the initliaize endpoint
  const init = useCallback(
    async (data: {
      ssn: string;
      bank: keyof typeof paymentServiceSuppliers;
    }) => {
      // Reset state if this is a retry
      reset();
      setState({ status: 'Loading' });

      try {
        const { id, autoStartTokenIsRequired } = await request<{
          id: string;
          autoStartTokenIsRequired: boolean;
        }>(`autogiro/retrieve-accounts-initialize`, {
          method: 'POST',
          body: {
            ...data,
            orderId,
          },
        });

        setCollectId(id);
        setState(prev => ({ ...prev, autoStartTokenIsRequired }));
      } catch (error) {
        setState({ status: 'Failed', error: ensureErrorWithMessage(error) });
      }
    },
    [orderId, reset],
  );

  // Collect info by polling the collect endpoint
  const collect = useCallback(async () => {
    if (!shouldPoll(state.status)) {
      return;
    }
    try {
      const response = await request<
        Omit<AutogiroResponseState, 'error'> & {
          errorCode?: string;
          errorMessage?: string;
        }
      >(`autogiro/retrieve-accounts-collect/${collectId}`);

      const { errorCode, errorMessage, ...rest } = response;

      const state: AutogiroResponseState = rest;

      if (response.status === 'Failed') {
        state.error = ensureErrorWithMessage(
          `${
            errorMessage || 'Ett oväntat fel inträffade'
          } (code: ${errorCode})`,
        );
      } else {
        setState(response);
      }
    } catch (error) {
      setState({ status: 'Failed', error: ensureErrorWithMessage(error) });
    }
  }, [collectId, state.status]);

  // Start the interval
  useEffect(() => {
    if (shouldPoll(state.status) && collectId != null) {
      collect();
      const id = setInterval(collect, pollingInterval);
      return () => {
        // Clear the interval on unmount or deps change
        clearInterval(id);
      };
    }
  }, [collect, collectId, state.status]);

  return [init, state, reset] as const;
};

export default useGetBankAccounts;
